import ActionTypes from './ActionTypes';
import ProductGroupAtions from '~/ProductGroup/ActionTypes';
import Meta from '~/Api/User/ProductGroup/Product/Meta';
import { processQuery, Product, type Query } from '~/Product';
import {
	Filter,
	retryFetchAfterUserProductGroupLock,
	UserProductGroup,
	UserProductGroupErrorResponse,
} from '~/Api/User/ProductGroup';
import { type Selection } from './actions';
import { invokeWithAccessToken } from '~/AccessToken';
import { logGoogleTagManagerGroupProductsAdd } from '~/GoogleTagManager';
import { flashErrorMessage, flashSuccessMessage } from '~/FlashMessage';
import { handleError } from '~/ErrorHandler';
import { createGroupAddedFlashMessage } from '~/ProductGroupPanel/Helpers/GroupFlashMessages';
import { SelectionTypes } from './actions';

import type { DispatchGetState } from '~/Store';
import type { PublicApi } from '~/Types/helpers';
import type UserProductGroupApi from '~/Api/User/ProductGroup/UserProductGroupApi';
import { ReceiveAddToGroupAction } from './reducer';

export type IGroupProductManager = PublicApi<GroupProductManager>;

export default class GroupProductManager implements IGroupProductManager {
	private store: DispatchGetState;
	private userProductGroupApi: UserProductGroupApi;

	constructor(store: DispatchGetState, services: { userProductGroupApi: UserProductGroupApi }) {
		this.store = store;
		this.userProductGroupApi = services.userProductGroupApi;
	}

	async handleProductGroupAddition(
		group: UserProductGroup,
		selection: Selection,
		options: {
			flashMessageText?: string;
		} = {}
	): Promise<void> {
		try {
			await this.addSelectionToGroup(group, selection);
			if (options.flashMessageText) {
				this.store.dispatch(
					flashSuccessMessage(
						createGroupAddedFlashMessage({
							group,
							text: options.flashMessageText,
						})
					)
				);
			}

			logGoogleTagManagerGroupProductsAdd(
				group.id,
				selection.type === SelectionTypes.products ? Array.from(selection.products) : null,
				null
			);
		} catch (response) {
			if (!(response instanceof UserProductGroupErrorResponse)) {
				this.store.dispatch(handleError(response));
				return;
			}
			if (response.handleProductGroupNotFound()) {
				this.fetchGroups(new Filter(group.parent?.id || null, null), 1);
			}
			if (response.handleProductGroupAddItemsLimit()) {
				const error = response.findAddItemsLimitError();
				this.store.dispatch(
					flashErrorMessage(
						`Cannot add more than ${error?.limit || '~2000'} products at once, try add less products in more steps`
					)
				);
			}
			this.store.dispatch(handleError(response));
			this.store.dispatch(handleError(response));
		}
	}

	async addSelectionToGroup(group: UserProductGroup, selection: Selection) {
		await (selection.type === SelectionTypes.all
			? this.addProductsBySearch(group, selection.query)
			: this.addProductsToGroup(group, Array.from(selection.products), new Meta(selection.query.filterParameters)));

		this.store.dispatch(
			invokeWithAccessToken((accessToken) => {
				const { recentStorage } = this.store.getState().productGroup;
				if (group.parent) {
					recentStorage.addGroupId(accessToken.user.id, group.parent.id);
				} else if (group.isGroup()) {
					recentStorage.addGroupId(accessToken.user.id, group.id);
				}
			})
		);
	}

	async addProductsBySearch(group: UserProductGroup, query: Query) {
		this.store.dispatch({ type: ActionTypes.requestAddToGroupBySearch, group });

		await retryFetchAfterUserProductGroupLock(() =>
			this.userProductGroupApi.addProductsBySearch(group.id, processQuery(query))
		);

		this.store.dispatch({ type: ActionTypes.receiveAddToGroupBySearch, group });
	}

	async addProductsToGroup(group: UserProductGroup, products: Product[], meta: Meta | null) {
		this.store.dispatch({ type: ActionTypes.requestAddToGroup, group });

		await retryFetchAfterUserProductGroupLock(() =>
			this.userProductGroupApi.addProductsToUserGroup(
				group.id,
				products.map((product) => ({
					id: product.id,
					packageSizeForSingleUnit: null,
				})),
				meta
			)
		);
		this.store.dispatch<ReceiveAddToGroupAction>({ type: ActionTypes.receiveAddToGroup, group, products });
	}

	async fetchGroups(filter: Filter = new Filter(null, null), page = 1, perPage = 100) {
		const request = this.userProductGroupApi.findUserProductGroups(filter, page, perPage);
		this.store.dispatch({ type: ProductGroupAtions.requestGroups, request, filter });

		try {
			const response = await request;
			this.store.dispatch({
				type: ProductGroupAtions.receiveGroups,
				request,
				filter,
				groups: response.items,
				pagination: response.pagination,
			});
		} catch (error) {
			this.store.dispatch(handleError(error));
			this.store.dispatch({
				type: ProductGroupAtions.receiveGroups,
				request,
				filter,
				groups: null,
				pagination: null,
			});
		}
	}
}
